/* This example requires Tailwind CSS v2.0+ */
import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import ButtonModal from "../../components/clientServiceSubscriptionTask/updateClientServiceSubscriptionTaskModal/ButtonModal";
import DeleteModal from "../../utils/DeleteModal";
import { getClientServiceRecurrencePayload } from "../../helpers/clientServiceRecurrence";
import { normalizeClientServiceInterval } from "../../helpers/intervalTypesMap";
import {
    findClientTeamForServiceDepartment,
    getClientServiceSubscriptionId,
    getClientServiceName,
    getDepartmentId,
    getDepartmentName,
    getTeamAssignmentIssue,
    getTeamAssignmentIssueType,
    getTeamConfigurationGuidance,
    getTeamId,
    getTeamName,
} from "../../helpers/serviceTeamAssignment";
import { updateClientService } from "../../services/clientService/updateClientService";
import { getClient } from "../../services/getClient";
import EfileStatusListBox from "./EfileStatusListBox";
import IntervalListBox from "./IntervalListBox";
import { usePermissions } from "../../context/PermissionsContext";
import { getClientServiceMessage } from "../../helpers/clientServiceMessage";

const ONE_TIME_INTERVAL = "One-time";

function normalizeInterval(value) {
    // Accept string, object, or event-like values
    if (!value) return "";

    if (typeof value === "string") return normalizeClientServiceInterval(value);

    // Common shapes: { name }, { value }, HeadlessUI passes the selected option object
    if (typeof value === "object") {
        if (typeof value.name === "string")
            return normalizeClientServiceInterval(value.name);
        if (typeof value.value === "string")
            return normalizeClientServiceInterval(value.value);
        if (value.target && typeof value.target.value === "string")
            return normalizeClientServiceInterval(value.target.value);
    }

    return "";
}

const EFILE_STATUSES = ["Needs Extension", "On Extension", "Accepted", "Rejected"];
const getServiceDepartmentId = (form) => {
    return getDepartmentId(form?.service) || getDepartmentId(form);
};

const getInitialClientServiceForm = (clientService) => {
    if (clientService && !Array.isArray(clientService)) {
        return {
            ...clientService,
            intervalType: normalizeClientServiceInterval(
                clientService.intervalType,
            ),
        };
    }

    return {
        id: null,
        endDate: "",
        efileStatus: "",
        isEfileStatusActive: false,
        isEveryItemRenewable: false,
        intervalType: "",
        team: null,
        year: new Date().getFullYear(),
        assignedBy: null,
        lastModifiedBy: null,
        completedBy: null,
    };
};

const UpdateClientServiceModal = ({
    open,
    setOpen,
    clientService,
    clientId,
    newClientService,
    loggedInEmployee,
    teamAssignmentRequired = false,
    teamAssignmentProgress,
    onSaveSuccess,
    onManageClientTeams,
    canDeleteService = false,
    onDeleteService,
}) => {
    const { can, permissionsLoading } = usePermissions();
    const canWriteServices = can("client_services.write");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [clientServiceForm, setClientServiceForm] = useState(() =>
        getInitialClientServiceForm(clientService),
    );
    const [clientTeams, setClientTeams] = useState([]);
    const [loadingClientTeams, setLoadingClientTeams] = useState(false);
    const [clientTeamsLoaded, setClientTeamsLoaded] = useState(false);
    const [teamHelperMessage, setTeamHelperMessage] = useState("");
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState("");
    const serviceDepartmentId = getServiceDepartmentId(clientServiceForm);
    const matchedClientTeam = clientTeamsLoaded
        ? findClientTeamForServiceDepartment(clientTeams, serviceDepartmentId)
        : null;
    const issueSource = {
        ...clientService,
        ...clientServiceForm,
    };
    const teamAssignmentIssueType = getTeamAssignmentIssueType(issueSource);
    const rawTeamAssignmentIssue = getTeamAssignmentIssue(issueSource);
    const canConfirmResolvedTeam = Boolean(
        clientTeamsLoaded &&
            matchedClientTeam &&
            teamAssignmentIssueType !== "missingTeamLead" &&
            teamAssignmentIssueType !== "multipleTeamLeads" &&
            teamAssignmentIssueType !== "missingDepartment",
    );
    const teamConfigurationGuidance = getTeamConfigurationGuidance({
        ...clientService,
        ...clientServiceForm,
        team: matchedClientTeam,
        teamAssignmentIssue:
            clientTeamsLoaded && serviceDepartmentId && !matchedClientTeam
                ? "MissingTeam"
                : rawTeamAssignmentIssue,
    });
    const resolvedTeamName = getTeamName(matchedClientTeam);
    const resolvedDepartmentName = getDepartmentName(
        clientServiceForm?.service || clientServiceForm,
    );
    const mandatoryWarningTitle = !clientTeamsLoaded || canConfirmResolvedTeam
        ? "Team assignment required"
        : "Team configuration required";
    const requiresCompatibleClientTeam = Boolean(
        teamAssignmentRequired &&
            clientTeamsLoaded &&
            serviceDepartmentId &&
            !matchedClientTeam,
    );
    const clientServiceName = getClientServiceName(clientServiceForm);
    const progressLabel =
        teamAssignmentProgress?.total > 1
            ? ` — ${teamAssignmentProgress.current} of ${teamAssignmentProgress.total}`
            : "";

    let completeButtonRef = useRef(null);

    /*  const loadManagers = useCallback(async () => {
        if (!clientId) return;
        const response = await getClientManagers(clientId);
        const list = Array.isArray(response?.data?.managers)
            ? response.data.managers
            : [];
        setManagers(list);

        setClientServiceForm((prev) => {
            if (!prev?.assignedEmployee) return prev;
            const stillExists = list.some(
                (m) => m.employeeId === prev.assignedEmployee.employeeId
            );
            if (stillExists) return prev;
            return { ...prev, assignedEmployee: null };
        });
    }, [clientId]); */

    /*  useEffect(() => {
        if (open) loadManagers();
    }, [open, loadManagers]); */

    useEffect(() => {
        setClientServiceForm(getInitialClientServiceForm(clientService));
    }, [clientService]);

    /* useEffect(() => {
        const getNewEmployees = async () => {
            const response = await getEmployees();
            setEmployees(response.data.employees);
        };
        getNewEmployees();
    }, []); */
    useEffect(() => {
        let isCurrent = true;

        if (!open || !clientId) {
            setClientTeams([]);
            setLoadingClientTeams(false);
            setClientTeamsLoaded(false);
            return;
        }

        const fetchClientTeams = async () => {
            setLoadingClientTeams(true);
            setClientTeamsLoaded(false);
            const response = await getClient(clientId);

            if (!isCurrent) return;

            if (response?.success) {
                setClientTeams(response.data?.teams || []);
            } else {
                setClientTeams([]);
                setTeamHelperMessage("Unable to load client teams.");
            }

            setLoadingClientTeams(false);
            setClientTeamsLoaded(true);
        };

        fetchClientTeams();

        return () => {
            isCurrent = false;
        };
    }, [open, clientId]);

    useEffect(() => {
        if (!open || !clientServiceForm) {
            setTeamHelperMessage("");
            return;
        }

        if (!serviceDepartmentId) {
            setTeamHelperMessage("Service department required");
            setClientServiceForm((prev) =>
                prev?.team ? { ...prev, team: null } : prev,
            );
            return;
        }

        if (loadingClientTeams) {
            setTeamHelperMessage("Loading client teams...");
            return;
        }

        const matchedTeam = findClientTeamForServiceDepartment(
            clientTeams,
            serviceDepartmentId,
        );

        setClientServiceForm((prev) => {
            if (!prev) return prev;

            const currentTeamId = getTeamId(prev.team);
            const matchedTeamId = getTeamId(matchedTeam);

            if (currentTeamId === matchedTeamId) return prev;

            return {
                ...prev,
                team: matchedTeam,
            };
        });

        if (!matchedTeam) {
            setTeamHelperMessage("No compatible team assigned");
        } else {
            setTeamHelperMessage("");
        }
    }, [
        open,
        clientServiceForm,
        clientTeams,
        loadingClientTeams,
        serviceDepartmentId,
    ]);

    const onHandleChangeYear = (e) => {
        setClientServiceForm({
            ...clientServiceForm,
            year: e.target.value,
        });
    };

    const onHandleChangeEfileStatus = (status) => {
        /**
         * Efile Statuses
         * 0 - Needs Extension
         * 1 - On Extension
         * 2 - Accepted
         * 3 - Rejected
         */

        setClientServiceForm({
            ...clientServiceForm,
            efileStatus: status,
        });
    };

    const onHandleChangeInterval = (interval) => {
        const nextInterval = normalizeInterval(interval);
        if (!nextInterval) return;

        setClientServiceForm((prev) => {
            if (!prev) return prev;

            // If selecting One-time -> force non-renewable
            if (nextInterval === ONE_TIME_INTERVAL) {
                return {
                    ...prev,
                    intervalType: ONE_TIME_INTERVAL,
                    isEveryItemRenewable: false,
                };
            }

            // If selecting a real interval while currently non-renewable,
            // automatically enable renewable again so the dropdown doesn't disappear.
            if (!prev.isEveryItemRenewable) {
                return {
                    ...prev,
                    intervalType: nextInterval,
                    isEveryItemRenewable: true,
                };
            }

            return {
                ...prev,
                intervalType: nextInterval,
            };
        });
    };

    /*   const onHandleChangeAssignEmployee = (newAssignedEmployee) => {
        setClientServiceForm({
            ...clientServiceForm,
            assignedEmployee: newAssignedEmployee,
        });
        console.log(clientServiceForm);
    }; */

    const onHandleChangeDueDate = (e) => {
        console.log(e.target.value);
        setClientServiceForm({ ...clientServiceForm, endDate: e.target.value });
    };

    /*  const validateForm = (form) => {
    console.log(form);
    const efilestatusnumber = EFILE_STATUSES.indexOf(form.efileStatus);
    console.log(efilestatusnumber);
    return true;
  }; */

    const onHandleSubmit = async (e) => {
        e.preventDefault();
        if (permissionsLoading || !canWriteServices || isSubmitting) {
            return;
        }

        if (!serviceDepartmentId) {
            toast.error(
                "This service is missing a department. Please update the service template before assigning a team.",
            );
            return;
        }

        if (loadingClientTeams) {
            toast.error("Please wait while the client's assigned teams load.");
            return;
        }

        const teamId = getTeamId(clientServiceForm.team);

        if (!teamId) {
            toast.error(
                "This client does not have a team assigned for the selected service department. Please assign the correct department team to the client before updating this service.",
            );
            return;
        }

        const interval = normalizeClientServiceInterval(
            (clientServiceForm.intervalType || "").trim(),
        );

        if (!interval) {
            toast.error("Please select an interval.");
            return;
        }

        if (clientServiceForm.isEveryItemRenewable) {
            if (interval === ONE_TIME_INTERVAL) {
                toast.error("Please select a valid interval.");
                return;
            }
        }

        const recurrencePayload = getClientServiceRecurrencePayload(interval);

        const payload = {
            ...clientServiceForm,
            isEveryItemRenewable: false,
            lastModifiedById: loggedInEmployee?.id || loggedInEmployee?.employeeId,
            recurrenceType: recurrencePayload.recurrenceType,
            intervalType: recurrencePayload.intervalType,
            clientServiceSubscriptionIntervalType: interval,
            team: clientServiceForm.team,
            teamId,
        };

        setIsSubmitting(true);
        try {
            const response = await updateClientService(
                clientServiceForm.id,
                payload,
            );
            const responseData = response?.data;
            const saveSucceeded =
                response?.success !== false && responseData?.success === true;

            if (!saveSucceeded) {
                if (response?.status !== 403) {
                    toast.error(
                        getClientServiceMessage(
                            responseData?.message || response?.message,
                            "The service could not be updated.",
                        ),
                    );
                }
                return;
            }

            let saveConfirmed = !teamAssignmentRequired;

            if (onSaveSuccess) {
                saveConfirmed =
                    (await onSaveSuccess({
                        response: responseData,
                        clientService: payload,
                    })) === true;
            } else if (newClientService) {
                await newClientService();
            }

            if (!saveConfirmed) return;

            toast.success(
                getClientServiceMessage(
                    responseData?.message,
                    "Service updated successfully.",
                ),
            );
            setOpen(false);
        } catch (error) {
            toast.error(
                getClientServiceMessage(
                    error?.message,
                    "The service could not be updated.",
                ),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const requestClose = () => {
        if (teamAssignmentRequired || isSubmitting) return;
        setOpen(false);
    };

    const setDeleteConfirmation = (isOpen) => {
        if (isDeleting) return;
        setDeleteError("");
        setDeleteConfirmationOpen(isOpen);
    };

    const handleDeleteService = async () => {
        if (!canDeleteService || !onDeleteService || isDeleting) return;

        setDeleteError("");
        setIsDeleting(true);
        try {
            const result = await onDeleteService(
                getClientServiceSubscriptionId(clientServiceForm),
            );
            const succeeded = result === true || result?.success === true;

            if (!succeeded) {
                setDeleteError(
                    getClientServiceMessage(
                        result?.message,
                        "The service could not be deleted. Please try again.",
                    ),
                );
                return;
            }

            setDeleteConfirmationOpen(false);
        } catch (error) {
            setDeleteError(
                getClientServiceMessage(
                    error?.message,
                    "The service could not be deleted. Please try again.",
                ),
            );
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <DeleteModal
                title="Delete service?"
                isModalOpen={deleteConfirmationOpen}
                setIsModalOpen={setDeleteConfirmation}
                onRemove={handleDeleteService}
                buttonText="Delete Service"
                itemName={clientServiceName}
                message="This will permanently delete this service and its related data. This action cannot be undone."
                errorMessage={deleteError}
                isDeleting={isDeleting}
                destructive={true}
                loadingButtonText="Deleting..."
                dialogClassName="z-[60]"
            />
            <AnimatePresence>
            {open && (
                <Dialog
                    initialFocus={completeButtonRef}
                    static
                    as={motion.div}
                    open={open}
                    onClose={requestClose}
                    className="relative z-50"
                >
                    {/* The backdrop, rendered as a fixed sibling to the panel container */}
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

                    {/* Full-screen scrollable container */}
                    <div className="fixed inset-0 w-screen overflow-y-auto">
                        {/* Container to center the panel */}
                        <div className="flex items-center justify-center min-h-full p-4">
                            <Dialog.Panel className="relative w-full px-4 pt-5 pb-4 overflow-hidden text-left transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:max-w-lg sm:p-6">
                                <div className="text-center">
                                    <div className="flex justify-between w-full">
                                        <Dialog.Title className="w-full text-lg font-medium leading-6 text-center text-gray-900">
                                            {teamAssignmentRequired
                                                ? `${mandatoryWarningTitle}${progressLabel}`
                                                : "Update Service"}
                                        </Dialog.Title>
                                    </div>
                                </div>

                                <form onSubmit={onHandleSubmit}>
                                    {teamAssignmentRequired && (
                                        <div className="p-3 mt-5 text-left border rounded-md border-amber-300 bg-amber-50">
                                            <p className="text-sm font-medium text-amber-900">
                                                {clientServiceName}
                                            </p>
                                            <p className="mt-2 text-xs text-amber-800">
                                                {!clientTeamsLoaded
                                                    ? "Checking the client's Teams configuration..."
                                                    : canConfirmResolvedTeam
                                                      ? `${resolvedTeamName} has been automatically selected from the ${resolvedDepartmentName} department.`
                                                      : teamConfigurationGuidance.issue}
                                            </p>
                                            {clientTeamsLoaded ? (
                                                <p className="mt-2 text-xs font-medium text-amber-800">
                                                    {canConfirmResolvedTeam
                                                        ? "Click Save to continue."
                                                        : teamConfigurationGuidance.action}
                                                </p>
                                            ) : null}
                                        </div>
                                    )}
                                    <div className="flex flex-col mt-5 sm:mt-6">
                                        <h1 className="font-normal text-md">
                                            Due Date
                                        </h1>
                                        <input
                                            onChange={onHandleChangeDueDate}
                                            type="date"
                                            id="endDate"
                                            name="endDate"
                                            className="pr-1 border border-gray-200 rounded-md form-select"
                                            value={
                                                (
                                                    clientServiceForm?.endDate || ""
                                                ).split("T")[0]
                                            }
                                        />
                                    </div>

                                    {clientServiceForm.isEfileStatusActive ===
                                        true && (
                                        <EfileStatusListBox
                                            efileStatuses={EFILE_STATUSES}
                                            selectedEfileStatus={
                                                clientServiceForm.efileStatus
                                            }
                                            onChange={(e) =>
                                                onHandleChangeEfileStatus(e)
                                            }
                                        />
                                    )}
                                    <IntervalListBox
                                        selectedInterval={
                                            clientServiceForm.intervalType
                                        }
                                        onChange={onHandleChangeInterval}
                                    />

                                    {clientServiceForm.intervalType ===
                                        ONE_TIME_INTERVAL && (
                                        <p className="mt-2 text-xs text-gray-500">
                                            One-time disables auto-renewal for this
                                            service.
                                        </p>
                                    )}

                                    {/*   <AssignedByListBox
                                        label="Assigned to Manager"
                                        employees={managers || []}
                                        assignedBy={
                                            clientServiceForm.assignedEmployee
                                        }
                                        onChange={onHandleChangeAssignEmployee}
                                    /> */}

                                    <div className="mt-5 sm:mt-6">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Team{" "}
                                            <span className="text-red-500">*</span>
                                        </label>
                                        <div
                                            className={`mt-1 rounded-md border px-3 py-2 text-sm ${
                                                clientServiceForm.team
                                                    ? "border-gray-300 bg-gray-100 text-gray-700"
                                                    : serviceDepartmentId
                                                      ? "border-red-300 bg-red-50 text-red-600"
                                                      : "border-gray-300 bg-gray-100 text-slate-500"
                                            }`}
                                        >
                                            {clientServiceForm.team ? (
                                                <div>
                                                    <div className="font-medium text-slate-800">
                                                        {getTeamName(
                                                            clientServiceForm.team,
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-slate-500">
                                                        Automatically assigned from
                                                        the client's{" "}
                                                        {getDepartmentName(
                                                            clientServiceForm.team,
                                                        )}{" "}
                                                        department team.
                                                    </div>
                                                </div>
                                            ) : serviceDepartmentId ? (
                                                <span>
                                                    {teamHelperMessage ||
                                                        "No compatible team assigned"}
                                                </span>
                                            ) : (
                                                <span>
                                                    This service is missing a
                                                    department.
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-5 sm:mt-6">
                                        <h1 className="font-normal text-md">Year</h1>
                                        <input
                                            onChange={(e) => onHandleChangeYear(e)}
                                            className="relative inline-block w-full text-left"
                                            type="number"
                                            min="2000"
                                            max="2100"
                                            step="1"
                                            value={clientServiceForm.year}
                                        />
                                    </div>

                                    <div className="mt-5 flex flex-col gap-3 sm:mt-6 sm:flex-row sm:items-center sm:justify-between">
                                        <div>
                                            {teamAssignmentRequired &&
                                            canDeleteService &&
                                            onDeleteService ? (
                                                <ButtonModal
                                                    name="Delete Service"
                                                    type="button"
                                                    onClick={() =>
                                                        setDeleteConfirmation(true)
                                                    }
                                                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:text-sm"
                                                />
                                            ) : null}
                                        </div>
                                        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
                                            {teamAssignmentRequired &&
                                            clientTeamsLoaded &&
                                            onManageClientTeams ? (
                                                <ButtonModal
                                                    name="Edit Client Teams"
                                                    type="button"
                                                    onClick={onManageClientTeams}
                                                    className={
                                                        requiresCompatibleClientTeam
                                                            ? "inline-flex w-full justify-center rounded-md border border-transparent bg-teal-500 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0 sm:w-auto sm:text-sm"
                                                            : "inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0 sm:w-auto sm:text-sm"
                                                    }
                                                />
                                            ) : null}
                                            {!requiresCompatibleClientTeam ? (
                                                <ButtonModal
                                                    name="Save"
                                                    type="submit"
                                                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-teal-500 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0 sm:w-auto sm:text-sm"
                                                    disabled={
                                                        permissionsLoading ||
                                                        !canWriteServices ||
                                                        isSubmitting
                                                    }
                                                />
                                            ) : null}
                                            {!teamAssignmentRequired && (
                                                <ButtonModal
                                                    name="Cancel"
                                                    type="button"
                                                    onClick={requestClose}
                                                    className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0 sm:w-auto sm:text-sm"
                                                />
                                            )}
                                        </div>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </div>
                    </div>
                </Dialog>
            )}
            </AnimatePresence>
        </>
    );
};
export default UpdateClientServiceModal;
